import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageProps } from '@/types'
import { DateRange } from '@/components/DateRangeFilter'
import DataTable, { Column } from '@/components/DataTable'
import ReportControlBar, { makeSortHandler } from '@/components/ReportControlBar'
import { ChartCard, TimeSeriesBarChart } from '@/components/ReportCharts'

interface Row {
  id: number
  agent_name: string
  agent_email: string
  sponsor_name: string
  sponsor_id: number | null
  contract_signed_at: string
  onboarding_status: string
  days_invite_to_signed: number | null
}

interface SeriesPoint {
  label: string
  value: number
}

interface Props extends PageProps {
  rows: Row[]
  series: SeriesPoint[]
  series_granularity: 'week' | 'month'
  date_range: DateRange
  sort: { key: string | null; dir: string | null }
}

const PATH = '/admin/reports/new_agents'

function fmt(d: string | null) { return d ? new Date(d).toLocaleDateString() : '—' }

export default function NewAgents({ rows, series, series_granularity, date_range, sort }: Props) {
  const onSortChange = makeSortHandler(PATH, date_range, {})
  const totalSigned = series.reduce((sum, s) => sum + s.value, 0)
  const bucketLabel = series_granularity
  const columns: Column<Row>[] = [
    { key: 'sponsor_name', header: 'Sponsor', sortable: true },
    { key: 'agent_name', header: 'New Agent', sortable: true,
      render: (r) => (
        <div>
          <div className="font-medium text-chestnut-charcoal">{r.agent_name}</div>
          <div className="text-xs text-chestnut-charcoal/60">{r.agent_email}</div>
        </div>
      ) },
    { key: 'contract_signed_at', header: 'Signed Date', sortable: true,
      render: (r) => fmt(r.contract_signed_at) },
    { key: 'onboarding_status', header: 'Status', sortable: true },
    { key: 'days_invite_to_signed', header: 'Days Invite → Signed', sortable: true, className: 'text-center',
      render: (r) => r.days_invite_to_signed ?? '—' }
  ]

  return (
    <AppLayout>
      <Head title="New Agents Onboarded" />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">New Agents Onboarded</h1>
          <p className="text-chestnut-charcoal/70 mt-1">
            Agents whose contract was signed OR who became active in the date range, grouped by sponsor.
            <span className="text-chestnut-charcoal/50"> "Days Invite → Signed" uses the matching referral's created_at when available.</span>
          </p>
        </div>

        <ReportControlBar path={PATH} dateRange={date_range} sortState={sort} />

        <ChartCard
          title={`New agents signed per ${bucketLabel}`}
          subtitle={totalSigned > 0
            ? `${totalSigned.toLocaleString()} agent${totalSigned === 1 ? '' : 's'} across ${series.length} ${bucketLabel}${series.length === 1 ? '' : 's'} in the selected range.`
            : undefined}
          empty={series.length === 0}
          emptyMessage="No agents signed in this date range."
        >
          <TimeSeriesBarChart data={series} tooltipLabel="Agents signed" />
        </ChartCard>

        <div className="bg-white rounded-xl border border-chestnut-gray p-6">
          <DataTable
            data={rows}
            columns={columns}
            searchPlaceholder="Search agents or sponsors..."
            searchKeys={['agent_name', 'agent_email', 'sponsor_name']}
            pageSize={20}
            emptyMessage="No agents signed in this date range."
            getRowKey={(r) => r.id}
            controlledSort={{ key: sort.key ?? 'contract_signed_at', direction: (sort.dir as 'asc' | 'desc') ?? 'desc' }}
            onSortChange={onSortChange}
          />
        </div>
      </div>
    </AppLayout>
  )
}
